// Step 6: Visit Summary - Review all assistances before final submission
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useVisitWizard } from '../../../../contexts/VisitWizardContext'
import { useUpdateVisit, useVisit } from '../../../../hooks/useVisits'
import { Button } from '../../../../components/ui/button'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../components/ui/accordion'
import { Badge } from '../../../../components/ui/badge'
import { Loader2, CheckCircle2,  Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { ASSISTANCE_TYPE_LABELS } from '../../../../types/visit-wizard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog'

export function VisitSummary() {
  const navigate = useNavigate()
  const { state, resetWizard, removeAssistance, goToStep } = useVisitWizard()
  const updateVisit = useUpdateVisit()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  // Fetch current visit data
  const { data: visitData } = useVisit(state.visitId || '', { enabled: !!state.visitId })

  // Visit details state (date, time_in, time_out)
  const [visitDate, setVisitDate] = useState('')
  const [timeIn, setTimeIn] = useState('')
  const [timeOut, setTimeOut] = useState('')

  // Initialize visit details with defaults
  useEffect(() => {
    if (visitData) {
      // Use existing values or defaults
      setVisitDate(visitData.visit_date || new Date().toISOString().split('T')[0])
      setTimeIn(visitData.time_in || new Date().toTimeString().split(' ')[0].substring(0, 5))

      // Set time_out to current time + 5 minutes (matching legacy behavior)
      const now = new Date()
      const timeOutDate = new Date(now.getTime() + 5 * 60000)
      setTimeOut(visitData.time_out || timeOutDate.toTimeString().split(' ')[0].substring(0, 5))
    } else {
      // Set defaults if visit not loaded yet
      const now = new Date()
      setVisitDate(now.toISOString().split('T')[0])
      setTimeIn(now.toTimeString().split(' ')[0].substring(0, 5))

      const timeOutDate = new Date(now.getTime() + 5 * 60000)
      setTimeOut(timeOutDate.toTimeString().split(' ')[0].substring(0, 5))
    }
  }, [visitData])

  // Check if this is a denied visit
  const isDenied = state.classification?.decision === 'deny' || state.denialReason !== null

  const handleSubmit = async () => {
    if (!state.visitId) {
      toast.error('No visit ID found')
      return
    }

    // For denied visits, skip assistance check
    if (!isDenied && state.assistances.length === 0) {
      toast.error('Please add at least one assistance')
      return
    }

    // Validate that date and times are filled
    if (!visitDate || !timeIn || !timeOut) {
      toast.error('Please fill in visit date, time in, and time out')
      return
    }

    setIsSubmitting(true)

    try {
      // Update visit with date and times only (matches legacy behavior)
      // Note: Status is already set during classification (100=ASSISTED, 101=DENIED)
      await updateVisit.mutateAsync({
        id: state.visitId,
        data: {
          visit_date: visitDate,
          time_in: timeIn,
          time_out: timeOut,
        },
      })

      toast.success(isDenied ? 'Visit denial recorded successfully!' : 'Visit completed successfully!')
      resetWizard()
      navigate('/visits')
    } catch (error: any) {
      console.error('Failed to complete visit:', error)
      toast.error(error.message || 'Failed to complete visit')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (deleteId) {
      removeAssistance(deleteId)
      setDeleteId(null)
      toast.success('Assistance removed')
    }
  }

  const handleAddMore = () => {
    goToStep('assistance-selection')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary underline mb-4">Summary</h2>
        <p className="text-muted-foreground">
          Review all assistances before final submission
        </p>
      </div>

      {/* Client Information */}
      <Card>
        <CardHeader>
          <CardTitle>Client Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Name</p>
              <p className="font-semibold">
                {state.selectedClient?.firstname} {state.selectedClient?.middlename || ''} {state.selectedClient?.lastname}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Age</p>
              <p className="font-semibold">{state.selectedClient?.birthday ? new Date().getFullYear() - new Date(state.selectedClient.birthday).getFullYear() : "N/A"} years old</p>
            </div>
            <div>
              <p className="text-muted-foreground">Gender</p>
              <p className="font-semibold">{state.selectedClient?.gender}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Contact</p>
              <p className="font-semibold">{state.selectedClient?.contact_number || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Classification Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Client Classifications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {state.classification?.client_classifications &&
             state.classification.client_classifications.length > 0 ? (
              state.classification.client_classifications.map((classification) => (
                <Badge key={classification} variant="secondary">
                  {classification}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-muted-foreground">No classifications selected</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Denial Reasons (shown if visit was denied) */}
      {isDenied && state.denialReason && (
        <Card>
          <CardHeader>
            <CardTitle className="text-accent">Denial Reasons</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {state.denialReason.reasons.map((reason, index) => (
                <div key={index} className="border-l-4 border-accent pl-4 py-2">
                  <p className="font-semibold text-sm">{reason.option}</p>
                  {reason.reason && (
                    <p className="text-sm text-muted-foreground mt-1">{reason.reason}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assistances (only shown if not denied) */}
      {!isDenied && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">
              Assistances Provided ({state.assistances.length})
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddMore}
              className="border-primary text-primary hover:bg-primary hover:text-white"
            >
              Add More
            </Button>
          </div>

        {state.assistances.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center p-12 text-center">
              <p className="text-lg font-medium text-muted-foreground mb-2">
                No assistances added yet
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Add at least one assistance to proceed
              </p>
              <Button onClick={handleAddMore} className="bg-primary hover:bg-primary/90">
                Add Assistance
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Accordion type="multiple" className="w-full">
            {state.assistances.map((assistance, index) => (
              <AccordionItem key={assistance.id} value={`item-${index}`}>
                <AccordionTrigger className="text-lg font-bold bg-primary text-white px-5 hover:no-underline rounded-t-lg">
                  <div className="flex justify-between items-center w-full pr-4">
                    <span>{ASSISTANCE_TYPE_LABELS[assistance.type]}</span>
                    <div className="flex items-center gap-2">
                      {assistance.control_number && (
                        <Badge variant="secondary" className="bg-white text-primary">
                          {assistance.control_number}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Saved
                      </Badge>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 py-4 border">
                  <div className="space-y-3">
                    {/* Display assistance data */}
                    {Object.entries(assistance.data).map(([key, value]) => (
                      <div key={key} className="flex border-b pb-2">
                        <dt className="w-1/3 text-sm font-medium text-muted-foreground capitalize">
                          {key.replace(/_/g, ' ')}:
                        </dt>
                        <dd className="w-2/3 text-sm">{String(value) || 'N/A'}</dd>
                      </div>
                    ))}

                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteId(assistance.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
        </div>
      )}

      {/* Date and Time Fields */}
      {(isDenied || state.assistances.length > 0) && (
        <div className="flex flex-col md:flex-row items-end justify-center gap-3 pt-6">
          <div className="space-y-2">
            <Label htmlFor="visit-date">Date</Label>
            <Input
              id="visit-date"
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
              className="w-full md:w-auto"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="time-in">Time In</Label>
            <Input
              id="time-in"
              type="time"
              value={timeIn}
              onChange={(e) => setTimeIn(e.target.value)}
              required
              className="w-full md:w-auto"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="time-out">Time Out</Label>
            <Input
              id="time-out"
              type="time"
              value={timeOut}
              onChange={(e) => setTimeOut(e.target.value)}
              required
              className="w-full md:w-auto"
            />
          </div>
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90 px-12 w-full md:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {isDenied ? 'Finish Visit' : 'Submit Visit'}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Assistance?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this assistance? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
